import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Copy, Check, X, FileText, Code, Maximize2, Minimize2, Globe, Palette, BarChart3, GitBranch } from 'lucide-react';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeRaw from 'rehype-raw';
import rehypeKatex from 'rehype-katex';
import remarkBreaks from 'remark-breaks';
import 'katex/dist/katex.min.css';
import { preprocessMath } from '../utils/mathUtils';
import { markdownToPlainText } from '../utils/clipboardText';
import ChatCodeBlock from './chat/CodeBlock';
import mermaid from 'mermaid';

// Enhanced SVG renderer component
const EnhancedSVGRenderer = ({ content }) => {
  const svgRef = useRef(null);
  const [error, setError] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!content || !svgRef.current) return;

    try {
      // Clean and validate SVG content
      const cleanContent = content.trim();
      
      console.log('SVG content preview:', cleanContent.substring(0, 200) + '...');
      
      // Basic validation - ensure it starts with SVG tag
      if (!cleanContent.toLowerCase().includes('<svg')) {
        throw new Error('Invalid SVG: Missing <svg> tag');
      }

      // Set the content
      svgRef.current.innerHTML = cleanContent;
      
      // Find the SVG element and ensure proper attributes
      const svgElement = svgRef.current.querySelector('svg');
      if (svgElement) {
        console.log('SVG element found, viewBox:', svgElement.getAttribute('viewBox'));
        console.log('SVG filters found:', svgElement.querySelectorAll('filter').length);
        console.log('SVG gradients found:', svgElement.querySelectorAll('linearGradient, radialGradient').length);
        
        // Ensure SVG has proper attributes for responsive rendering
        if (!svgElement.getAttribute('width') && !svgElement.getAttribute('height') && !svgElement.getAttribute('viewBox')) {
          svgElement.setAttribute('width', '100%');
          svgElement.setAttribute('height', 'auto');
        }
        
        // Add proper styling for complex SVGs
        svgElement.style.maxWidth = '100%';
        svgElement.style.height = 'auto';
        svgElement.style.display = 'block';
        
        // Force browser to re-evaluate the SVG
        setTimeout(() => {
          svgElement.style.visibility = 'hidden';
          // eslint-disable-next-line no-unused-expressions
          svgElement.offsetHeight; // trigger reflow
          svgElement.style.visibility = 'visible';
        }, 10);
      }

      setError(null);
      setIsLoaded(true);
    } catch (err) {
      console.error('SVG rendering error:', err);
      setError(err.message);
      setIsLoaded(false);
    }
  }, [content]);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 p-4 text-center">
        <div className="text-red-500 mb-2">SVG Rendering Error</div>
        <div className="text-sm text-gray-600 dark:text-gray-400">{error}</div>
        <details className="mt-4 max-w-full">
          <summary className="cursor-pointer text-sm text-blue-500">Show raw SVG</summary>
          <pre className="mt-2 p-2 bg-gray-100 dark:bg-gray-800 rounded text-xs overflow-auto max-h-40">
            {content}
          </pre>
        </details>
      </div>
    );
  }

  return (
    <div 
      ref={svgRef}
      className="svg-container p-4"
      style={{ 
        background: 'transparent',
        overflow: 'visible'
      }}
    />
  );
};

// Mermaid component for rendering diagrams
const MermaidDiagram = ({ content }) => {
  const ref = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    if (!isInitialized) {
      mermaid.initialize({ 
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
      });
      setIsInitialized(true);
    }
  }, [isInitialized]);

  useEffect(() => {
    if (ref.current && isInitialized && content) {
      const renderDiagram = async () => {
        try {
          const id = `mermaid-${Date.now()}`;
          const { svg } = await mermaid.render(id, content);
          ref.current.innerHTML = svg;
        } catch (error) {
          console.error('Mermaid rendering error:', error);
          ref.current.innerHTML = `<pre className="text-red-500">Error rendering diagram: ${error.message}</pre>`;
        }
      };
      renderDiagram();
    }
  }, [content, isInitialized]);

  return <div ref={ref} className="mermaid-diagram w-full" />;
};

const ArtifactRenderer = ({ artifact, onClose, isExpanded, onToggleExpanded }) => {
  const [copyStatus, setCopyStatus] = useState(false);

  const handleCopy = async () => {
    try {
      const copyText = artifact.type === 'markdown'
        ? markdownToPlainText(artifact.content)
        : artifact.content;

      await navigator.clipboard.writeText(copyText);
      setCopyStatus(true);
      setTimeout(() => setCopyStatus(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const getIcon = () => {
    switch (artifact.type) {
      case 'markdown':
        return <FileText size={16} />;
      case 'code':
        return <Code size={16} />;
      case 'html':
        return <Globe size={16} />;
      case 'react':
        return <Code size={16} />;
      case 'svg':
        return <Palette size={16} />;
      case 'css':
        return <Palette size={16} />;
      case 'chart':
        return <BarChart3 size={16} />;
      case 'mermaid':
        return <GitBranch size={16} />;
      case 'text':
      default:
        return <FileText size={16} />;
    }
  };

  const getLanguage = useCallback(() => {
    if (artifact.language) return artifact.language;
    switch (artifact.type) {
      case 'code':
        return 'javascript';
      case 'html':
        return 'html';
      case 'react':
        return 'jsx';
      case 'svg':
        return 'xml';
      case 'css':
        return 'css';
      case 'markdown':
        return 'markdown';
      case 'mermaid':
        return 'mermaid';
      default:
        return 'text';
    }
  }, [artifact.language, artifact.type]);

  const renderContent = useMemo(() => {
    if (artifact.type === 'markdown') {
      return (
        <ReactMarkdown
          className="markdown-content leading-relaxed text-base text-black dark:text-gray-200"
          remarkPlugins={[remarkGfm, remarkMath, remarkBreaks]}
          rehypePlugins={[rehypeRaw, rehypeKatex]}
          components={{
            p: ({ node, ...props }) => (
              <p className="mb-3 last:mb-0 leading-loose" {...props} />
            ),
            a: ({ node, ...props }) => (
              // eslint-disable-next-line jsx-a11y/anchor-has-content
              <a
                className="text-blue-600 hover:underline dark:text-blue-400 break-words"
                target="_blank"
                rel="noopener noreferrer"
                {...props}
              />
            ),
            h1: ({ node, ...props }) => (
              // eslint-disable-next-line jsx-a11y/heading-has-content
              <h1
                className="text-2xl font-bold mt-1 mb-3  pb-1"
                {...props}
              />
            ),
            h2: ({ node, ...props }) => (
              // eslint-disable-next-line jsx-a11y/heading-has-content
              <h2 className="text-xl font-semibold mt-5 mb-2" {...props} />
            ),
            h3: ({ node, ...props }) => (
              // eslint-disable-next-line jsx-a11y/heading-has-content
              <h3 className="text-lg font-medium mt-4 mb-1" {...props} />
            ),
            h4: ({ node, ...props }) => (
              // eslint-disable-next-line jsx-a11y/heading-has-content
              <h4 className="text-base font-medium mt-3 mb-1" {...props} />
            ),
            blockquote: ({ node, ...props }) => (
              <blockquote
                className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic text-gray-700 dark:text-gray-300 my-3"
                {...props}
              />
            ),
            ul: ({ node, ...props }) => (
              <ul className="list-disc list-outside ml-4 mb-3 space-y-1" {...props} />
            ),
            ol: ({ node, ...props }) => (
              <ol className="list-decimal list-outside ml-4 mb-3 space-y-1" {...props} />
            ),
            li: ({ node, ...props }) => (
              <li className="mb-1 pl-1" {...props} />
            ),
            table: ({ node, ...props }) => (
              <table
                className="border-collapse border border-gray-300 dark:border-gray-600 mt-4 mb-4 w-full text-left"
                {...props}
              />
            ),
            thead: ({ node, ...props }) => (
              <thead className="bg-gray-100 dark:bg-gray-700" {...props} />
            ),
            tr: ({ node, ...props }) => (
              <tr
                className="border border-gray-300 dark:border-gray-600"
                {...props}
              />
            ),
            th: ({ node, ...props }) => (
              <th
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 font-semibold bg-gray-50 dark:bg-gray-800"
                {...props}
              />
            ),
            td: ({ node, ...props }) => (
              <td
                className="px-3 py-2 border border-gray-300 dark:border-gray-600"
                {...props}
              />
            ),
            code: ChatCodeBlock,
            img: ({ node, ...props }) => (
              <img
                alt={props.alt || ""}
                {...props}
                className="my-3 max-w-full h-auto rounded-lg border border-gray-200 dark:border-gray-700"
                style={{
                  maxWidth: '300px',
                  maxHeight: '300px',
                  objectFit: 'contain',
                }}
              />
            ),
            svg: ({ node, ...props }) => (
              <svg {...props} />
            ),
            defs: ({ node, ...props }) => (
              <defs {...props} />
            ),
            path: ({ node, ...props }) => (
              <path {...props} />
            ),
            circle: ({ node, ...props }) => (
              <circle {...props} />
            ),
            rect: ({ node, ...props }) => (
              <rect {...props} />
            ),
            ellipse: ({ node, ...props }) => (
              <ellipse {...props} />
            ),
            g: ({ node, ...props }) => (
              <g {...props} />
            ),
            text: ({ node, ...props }) => (
              <text {...props} />
            ),
            linearGradient: ({ node, ...props }) => (
              <linearGradient {...props} />
            ),
            radialGradient: ({ node, ...props }) => (
              <radialGradient {...props} />
            ),
            stop: ({ node, ...props }) => (
              <stop {...props} />
            ),
            filter: ({ node, ...props }) => (
              <filter {...props} />
            ),
            feDropShadow: ({ node, ...props }) => (
              <feDropShadow {...props} />
            ),
            feGaussianBlur: ({ node, ...props }) => (
              <feGaussianBlur {...props} />
            ),
            pattern: ({ node, ...props }) => (
              <pattern {...props} />
            ),
          }}
        >
          {preprocessMath(artifact.content)}
        </ReactMarkdown>
      );
    } else if (artifact.type === 'html') {
      return (
        <iframe
          srcDoc={artifact.content}
          className="w-full h-full min-h-96 border-0 bg-white"
          title="HTML Preview"
          sandbox="allow-scripts allow-same-origin"
        />
      );
    } else if (artifact.type === 'svg') {
      return <EnhancedSVGRenderer content={artifact.content} />;
    } else if (artifact.type === 'mermaid') {
      return <MermaidDiagram content={artifact.content} />;
    } else if (artifact.type === 'chart') {
      return (
        <div className="w-full h-full min-h-96 flex justify-center items-center">
          <pre className="text-base leading-relaxed font-mono whitespace-pre-wrap theme-text-primary">
            {artifact.content}
          </pre>
        </div>
      );
    } else if (['code', 'react', 'css'].includes(artifact.type)) {
      return (
        <SyntaxHighlighter
          style={vscDarkPlus}
          language={getLanguage()}
          PreTag="div"
          customStyle={{ margin: 0, padding: '1rem' }}
          wrapLongLines={true}
        >
          {artifact.content}
        </SyntaxHighlighter>
      );
    } else {
      return (
        <pre className="text-base leading-relaxed font-mono whitespace-pre-wrap theme-text-primary">
          {artifact.content}
        </pre>
      );
    }
  }, [artifact.content, artifact.type, getLanguage]);

  return (
    <div className={`flex flex-col h-full w-full transition-all duration-300 ${
      ['code', 'react', 'css'].includes(artifact.type) ? 'theme-code' : 'theme-card-bg'
    }`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2 min-w-0">
          {getIcon()}
          <h3 className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {artifact.title}
          </h3>
          {/* {artifact.language && (
            <span className="text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-gray-600 dark:text-gray-400">
              {artifact.language}
            </span>
          )} */}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={onToggleExpanded}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
            title={isExpanded ? "Minimize" : "Maximize"}
          >
            {isExpanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </button>
          <button
            onClick={handleCopy}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
            title="Copy content"
          >
            {copyStatus ? <Check size={16} /> : <Copy size={16} />}
          </button>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400"
            title="Close"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className={`flex-1 overflow-auto ${['code', 'react', 'css', 'html', 'svg', 'mermaid', 'chart'].includes(artifact.type) ? '' : 'p-4 pl-8'}`}>
        {renderContent}
      </div>
    </div>
  );
};

export default ArtifactRenderer;
